import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface BatchListItem {
  id: number;
  name: string | null;
  batchCode: string | null;
  batchShortCode: string | null;
  displayName: string | null;
  schoolId: number | null;
  schoolName: string | null;
  programId: number | null;
  programName: string | null;
  courseId: number | null;
  courseName: string | null;
  yearId: number | null;
  yearName: string | null;
  currentTermId: number | null;
  currentTermName: string | null;
  batchTypeId: number | null;
  batchTypeName: string | null;
  parentId: number | null;
  parentName: string | null;
  maxStrength: number | null;
  currentStrength: number;
  activeStatus: number | null;
  startDate: string | null;
  endDate: string | null;
  sortOrder: number | null;
  tags: { id: number; name: string }[];
  instructors: string[];
}

export interface BatchDetail {
  id: number;
  name: string | null;
  batchCode: string | null;
  batchShortCode: string | null;
  displayName: string | null;
  schoolId: number | null;
  schoolName: string | null;
  programId: number | null;
  programName: string | null;
  courseId: number | null;
  courseName: string | null;
  yearId: number | null;
  yearName: string | null;
  currentTermId: number | null;
  currentTermName: string | null;
  batchTypeId: number | null;
  batchTypeName: string | null;
  parentId: number | null;
  parentName: string | null;
  parentBatchCode: string | null;
  locationText: string | null;
  remarks: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  maxStrength: number | null;
  sortOrder: number | null;
  activeStatus: number | null;
  mediumId: number | null;
  sectionId: number | null;
  tagIds: number[];
  tags: { id: number; name: string; typeId: number | null; typeName: string | null }[];
}

export interface BatchFormData {
  name: string;
  batchCode: string | null;
  batchShortCode: string | null;
  displayName: string | null;
  schoolId: number | null;
  programId: number | null;
  courseId: number | null;
  yearId: number | null;
  currentTermId: number | null;
  batchTypeId: number | null;
  parentId: number | null;
  locationText: string | null;
  remarks: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  maxStrength: number | null;
  sortOrder: number | null;
  activeStatus?: number;
  tagIds: number[];
}

export interface BatchSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  schoolId?: number;
  programId?: number;
  courseId?: number;
  yearId?: number;
  currentTermId?: number;
  batchTypeId?: number;
  parentId?: number;
  tagId?: number;
  includeInactive?: boolean;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface BatchLookups {
  isProgramBased: boolean;
  schools: { id: number; name: string }[];
  years: { id: number; name: string }[];
  batchTypes: { id: number; name: string }[];
  parents: { id: number; name: string }[];
  programs: { id: number; name: string; code: string | null }[];
  programYears: { programId: number; yearId: number }[];
  programSchools: { programId: number; schoolId: number }[];
  terms: { id: number; name: string; programId: number | null }[];
  courses: { id: number; name: string }[];
  tags: { id: number; name: string; typeId: number | null; typeName: string | null }[];
}

export interface BatchListResponse {
  success: boolean;
  data: {
    isProgramBased: boolean;
    batches: BatchListItem[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

export interface BatchResponse {
  success: boolean;
  data?: BatchDetail;
  message?: string;
  error?: string;
}

class BatchManagementService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/batches/manage`;
  }

  async getLookups(): Promise<{ success: boolean; data: BatchLookups }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/lookups`);
      return response.data;
    } catch (error: any) {
      console.error("[BatchManagementService] Error fetching lookups:", error);
      return {
        success: false,
        data: {
          isProgramBased: false,
          schools: [],
          years: [],
          batchTypes: [],
          parents: [],
          programs: [],
          programYears: [],
          programSchools: [],
          terms: [],
          courses: [],
          tags: [],
        },
      };
    }
  }

  async searchBatches(params: BatchSearchParams): Promise<BatchListResponse> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/search`, { params });
      return response.data;
    } catch (error: any) {
      console.error("[BatchManagementService] Error searching batches:", error);
      return {
        success: false,
        data: {
          isProgramBased: false,
          batches: [],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        },
      };
    }
  }

  async getBatchById(id: number): Promise<BatchResponse> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("[BatchManagementService] Error fetching batch:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch batch",
      };
    }
  }

  async createBatch(data: BatchFormData): Promise<BatchResponse> {
    try {
      const response = await myAxios.post(this.baseUrl, data);
      return response.data as unknown as BatchResponse;
    } catch (error: any) {
      console.error("[BatchManagementService] Error creating batch:", error);
      const body = error.response?.data;
      const message =
        body?.error?.message || body?.message || "Failed to create batch";
      return { success: false, message };
    }
  }

  async updateBatch(id: number, data: BatchFormData): Promise<BatchResponse> {
    try {
      const response = await myAxios.put(`${this.baseUrl}/${id}`, data);
      return response.data as unknown as BatchResponse;
    } catch (error: any) {
      console.error("[BatchManagementService] Error updating batch:", error);
      const body = error.response?.data;
      const message =
        body?.error?.message || body?.message || "Failed to update batch";
      return { success: false, message };
    }
  }

  async deleteBatch(id: number): Promise<BatchResponse> {
    try {
      const response = await myAxios.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("[BatchManagementService] Error deleting batch:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete batch",
      };
    }
  }
}

export default new BatchManagementService();
